import { Link } from 'react-router-dom'

const LAST_UPDATED = 'May 2026'
const CONTACT = 'privacy@themarginsjournal.com'

export default function PrivacyPage() {
  return (
    <div style={s.page}>
      <div style={s.content}>
        <p style={s.eyebrow}>Legal</p>
        <h1 style={s.title}>Privacy Policy</h1>
        <p style={s.meta}>Last updated: {LAST_UPDATED}</p>

        <Section title="Who we are">
          The Margins Journal is a publishing platform operated by Shaurya Maheshwari and Savyaraj (The Margins Journal Team), based in Jaipur, India. Our website is at themarginsjournal.com. You can reach us at <a href={`mailto:${CONTACT}`} style={s.link}>{CONTACT}</a>.
        </Section>

        <Section title="What we collect and why">
          <b>Account information:</b> When you create an account, we store your email address, display name, and a hashed password (managed by Supabase Auth). We use this to identify you, let you sign in, and attribute your writing to you.
          <br /><br />
          <b>Content you create:</b> Posts, comments, reactions, annotations, and reading list saves are stored in our database (Supabase/Postgres hosted on Supabase's EU or US servers). This is what the platform runs on.
          <br /><br />
          <b>Usage analytics:</b> If you consent, we use Google Analytics 4 to understand how people use the platform — which pages get visited, how long people read, where they come from. This uses cookies. You can decline this when you first visit and the site works identically.
          <br /><br />
          <b>Technical data:</b> Our hosting provider (Hostinger) may log standard access logs (IP address, browser, timestamp) for security purposes. We do not use these for tracking.
        </Section>

        <Section title="What we do not do">
          We do not sell your data to anyone, ever. We do not run advertising. We do not build profiles of you for commercial purposes. We do not share your data with third parties except as described in this policy.
        </Section>

        <Section title="Cookies">
          We use two types of cookies. Session cookies (essential) keep you signed in — these are set by Supabase Auth and are required for the platform to function. Analytics cookies (optional) are set by Google Analytics if you consent. You can decline analytics cookies and still use the full platform. Your consent choice is stored in your browser's localStorage.
        </Section>

        <Section title="Data storage and security">
          Your data is stored on Supabase's infrastructure. Supabase uses Postgres with row-level security — your data is only accessible to you and platform administrators. Passwords are never stored in plain text. All connections use HTTPS/TLS. We use Supabase's built-in role-based access control to ensure that users can only access and modify their own data.
        </Section>

        <Section title="Your rights (GDPR)">
          If you are in the European Economic Area, you have the right to: access your personal data; correct inaccurate data; request deletion of your account and data; object to processing; and data portability (export your writing at any time from Dashboard → Export). To exercise any of these rights, email <a href={`mailto:${CONTACT}`} style={s.link}>{CONTACT}</a>.
        </Section>

        <Section title="Data retention">
          We keep your account data for as long as your account is active. If you delete your account, your profile, posts, and comments are deleted within 30 days. Analytics data is retained by Google for 14 months per their standard retention policy.
        </Section>

        <Section title="Children">
          The Margins Journal is intended for users aged 13 and over. If you believe a child under 13 has created an account, contact us and we will delete it promptly.
        </Section>

        <Section title="Changes to this policy">
          If we make material changes to this policy, we will notify signed-in users via the platform's notification system. Continued use after notification constitutes acceptance.
        </Section>

        <Section title="Contact">
          Privacy questions: <a href={`mailto:${CONTACT}`} style={s.link}>{CONTACT}</a>
        </Section>

        <p style={{ ...s.meta, marginTop: '3rem' }}>
          <Link to="/terms" style={s.link}>Terms of Service</Link>
          {' · '}
          <Link to="/support" style={s.link}>Support</Link>
        </p>
      </div>
    </div>
  )
}

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: '2rem' }}>
      <h2 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: '1.05rem', fontWeight: 700, marginBottom: '0.6rem', color: 'var(--ink)' }}>
        {title}
      </h2>
      <p style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: '0.92rem', color: 'var(--muted)', lineHeight: 1.8 }}>
        {children}
      </p>
    </div>
  )
}

const s = {
  page: { paddingTop: '4.5rem', minHeight: '100vh' },
  content: { maxWidth: 680, margin: '0 auto', padding: '4rem 2rem 6rem' },
  eyebrow: { fontFamily: "'DM Mono', monospace", fontSize: '0.62rem', letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--accent)', marginBottom: '0.75rem' },
  title: { fontFamily: "'Space Grotesk', sans-serif", fontSize: 'clamp(2rem, 5vw, 2.8rem)', fontWeight: 700, letterSpacing: '-0.02em', marginBottom: '0.5rem' },
  meta: { fontFamily: "'DM Mono', monospace", fontSize: '0.62rem', color: 'var(--muted)', marginBottom: '3rem' },
  link: { color: 'var(--accent)', textDecoration: 'underline' },
}
